// Toast notification system
type ToastType = 'success' | 'error' | 'warning' | 'info';
export type ToastPosition = 'top-right' | 'center';

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
  position?: ToastPosition;
}

export interface ShowToastOptions {
  position?: ToastPosition;
}

class ToastManager {
  private toasts: Toast[] = [];
  private listeners: Array<(toasts: Toast[]) => void> = [];

  subscribe(listener: (toasts: Toast[]) => void) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  private notify() {
    this.listeners.forEach((listener) => listener([...this.toasts]));
  }

  show(type: ToastType, message: string, duration = 5000, options?: ShowToastOptions) {
    const id = Math.random().toString(36).substr(2, 9);
    const position = options?.position ?? 'top-right';
    const toast: Toast = { id, type, message, duration, position };
    this.toasts.push(toast);
    this.notify();

    if (duration > 0) {
      setTimeout(() => {
        this.remove(id);
      }, duration);
    }

    return id;
  }

  remove(id: string) {
    this.toasts = this.toasts.filter((t) => t.id !== id);
    this.notify();
  }

  clear() {
    this.toasts = [];
    this.notify();
  }

  success(message: string, duration?: number, options?: ShowToastOptions) {
    return this.show('success', message, duration ?? 5000, options);
  }

  /** Show a success toast in the center of the page (e.g. for course/writing feedback). */
  successCenter(message: string, duration = 2000) {
    return this.show('success', message, duration, { position: 'center' });
  }

  error(message: string, duration?: number) {
    return this.show('error', message, duration);
  }

  warning(message: string, duration?: number) {
    return this.show('warning', message, duration);
  }

  info(message: string, duration?: number) {
    return this.show('info', message, duration);
  }
}

export const toastManager = new ToastManager();

